import loading from 'assets/pictures/loading.gif'
import { push } from '@lagunovsky/redux-react-router'
import React, { useEffect, useMemo } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import { sortBy } from 'lodash'
import MonitoredMaterielPortletPanel from '../../materiel/components/dashboard/MonitoredMaterielPortletPanel'
import { getUserBookmarksByStationType } from '../../utils/UserUtil'
import { componentHasHabilitations } from '../../utils/HabilitationUtil'
import {
    H_MAT_MODULE,
    H_PRODUCTION_MODULE,
} from '../../account/constants/AccessRulesConstants'
import HabilitationRequired from '../../utils/components/HabilitationRequired'
import { STATION_TYPE_CONSTANT, STATION_TYPE_NAME } from 'station/constants/StationConstants'
import UserThunk from 'administration/components/user/actions/UserThunk'
import useStateProgress from 'utils/customHook/useStateProgress'
import ProductionUnitThunk from 'productionUnit/actions/ProductionUnitThunk'
import ProductionUnitStats from './alerts/ProductionUnitIndicators'
import { Card, CardContent, Grid, Stack, Tooltip } from '@mui/material'
import { CardTitle } from '../../components/card/NewCard'
import LocationCityIcon from '@mui/icons-material/LocationCity'
import { darkBlue, favOrange } from '../../utils/constants/ColorTheme'
import ProgressBar from '../../components/progress/ProgressBar'
import { NewTable } from '../../components/datatable/NewTable'
import StarRoundedIcon from '@mui/icons-material/StarRounded'

const ProductionUnitCard = () => {
    const {
        userBookmarks,
        productionUnits,
        monitoredMateriels,
        citiesIndex,
        userBookmarksStatus,
        productionUnitsStatus,
    } = useSelector(store => ({
        userBookmarks: store.UserReducer.userBookmarks,
        productionUnits: store.ProductionUnitReducer.productionUnits,
        monitoredMateriels: store.MaterielReducer.monitoredMateriels,
        citiesIndex: store.CityReducer.citiesIndex,
        userBookmarksStatus: store.DataManagerReducer.user.userBookmarksStatus,
        productionUnitsStatus: store.DataManagerReducer.productionUnit.productionUnitsStatus,
    }), shallowEqual)

    const { progress: progressBar, isLoaded: dataLoaded } = useStateProgress([userBookmarksStatus, productionUnitsStatus])

    const dispatch = useDispatch()

    useEffect(() => {
        dispatch(UserThunk.fetchBookmarks())
        dispatch(ProductionUnitThunk.fetchProductionUnits())
    }, [])

    const bookmarks = useMemo(() => getUserBookmarksByStationType(userBookmarks, STATION_TYPE_NAME.productionUnit), [userBookmarks])

    const foundBookmarks = useMemo(() => bookmarks.reduce((acc, code) => {
        const station = productionUnits.find(s => s.code === code)
        if (station) {
            return [...acc, station]
        }
        return acc
    }, []), [productionUnits, bookmarks])

    const monitoredMaterielsFiltered = useMemo(() => monitoredMateriels.filter(m => m.typeSite === STATION_TYPE_CONSTANT.PRODUCTION), [monitoredMateriels])

    const bookmarksStationPanel = useMemo(() => {
        if (!foundBookmarks.length) {
            return null
        }
        const stationResult = sortBy(foundBookmarks.map(station => {
            const cityFound = citiesIndex[station.townCode]
            return {
                ...station,
                city: cityFound ? `${cityFound.name} [${cityFound.code}]` : station.townCode,
                headers: ['code', 'city', 'name', 'operator', 'buildingOwner'],
                nullValue: { value: <Tooltip title={i18n.bookmark}><StarRoundedIcon sx={{ color: favOrange }} /></Tooltip> },
            }
        }), 'name')
        return (
            <NewTable
                headers={['nullValue', 'code', 'city', 'name', 'operator', 'buildingOwner']}
                rows={stationResult}
                onClickRow={s => dispatch(push(`/station/${STATION_TYPE_NAME.productionUnit}/${s.id}`))}
            />
        )
    }, [citiesIndex, foundBookmarks])

    if (dataLoaded) {
        if (!(componentHasHabilitations(H_PRODUCTION_MODULE) && (
            foundBookmarks.length > 0
            || monitoredMaterielsFiltered.length > 0
        ))) {
            return null
        }
        return (
            <Card>
                <CardTitle
                    title={i18n[STATION_TYPE_NAME.productionUnit]}
                    icon={<LocationCityIcon fontSize='large' />}
                    color={darkBlue}
                    onClick={() => dispatch(push(`/${STATION_TYPE_NAME.productionUnit}`))}
                />
                <Stack sx={{ width: '100%' }} spacing={1}>
                    <ProductionUnitStats />
                    {bookmarksStationPanel && (
                        bookmarksStationPanel
                    )}
                    {monitoredMaterielsFiltered.length > 0 && (
                        <HabilitationRequired habilitation={H_MAT_MODULE}>
                            <MonitoredMaterielPortletPanel noHeader={true} stationType={STATION_TYPE_CONSTANT.PRODUCTION} />
                        </HabilitationRequired>
                    )}
                </Stack>
            </Card>
        )
    }
    return (
        <Card>
            <CardContent>
                <Grid
                    container
                    justifyContent='center'
                    alignItems='center'
                    direction='column'
                    sx={{ pt: 2.5 }}
                >
                    <img src={loading} />
                    <Grid size={3}>
                        <ProgressBar
                            withMessage
                            message={i18n.productionUnitCardInProgress}
                            progress={progressBar}
                        />
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    )
}

export default ProductionUnitCard